/* Manu AI v13 (20/10/2026) — le design d'abord, le vêtement ensuite
 * - Étape 1 « Ton design » : mockup générique blanc cassé (t-shirt oversize dessiné ici, aucune photo à charger),
 *   en slider Devant / Dos : une face par vue, onglets + flèches + glissement. Dans chaque vue : création (Créer avec
 *   Manu / Refaire), import, retrait, déplacement et taille du logo. Barre d'outils sous le t-shirt : plus de superposition.
 *   Zones à l'échelle (≈ 8,8 px/cm) : devant 28 × 12 cm en haut de poitrine, dos 28 × 32 cm sous le col.
 *   Bouton « OK pour ce design ».
 * - Étape 2 « Ton support » : vêtement + couleur sur les photos Shopify (éditeur v11, logos figés), prix + délai,
 *   « Valider ce vêtement » → récapitulatif (étape 3), inchangé. « Changer de support » y revient.
 * - Image du visiteur « à imprimer telle quelle » (chat ou import depuis l'accueil) : Manu demande sur quelle face.
 * - Plus de message de définition (dpi) sur les logos dessinés par Manu (affinés par l'atelier) ; gardé pour un fichier importé.
 * - Conversation : identifiant par création (paramètre conv de l'endpoint) + étapes clés envoyées au serveur
 *   (action « event »), pour l'analyse des conversations (stockage Vercel Blob, api/studio.js).
 * S'appuie sur ai-studio.js + v9 / v10 / v11 (section._ais), chargés avant. */
(function () {
  'use strict';

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }

  var T = {
    fr: { ok: 'OK pour ce design', lead: 'Place et ajuste tes logos devant et dos. Le vêtement et sa couleur se choisissent juste après.', cap: 'Aperçu générique', alt_front: 'T-shirt générique, devant', alt_back: 'T-shirt générique, dos', pick: 'Top ! Choisis maintenant ton vêtement et sa couleur.', validate: 'Valider ce vêtement', edit: 'Modifier le design', prev: 'Face précédente', next: 'Face suivante', views: 'Faces du t-shirt', which: 'Je le pose sur quelle face ?', empty: 'Rien pour l’instant' },
    en: { ok: 'OK, keep this design', lead: 'Place and resize your front and back logos. You’ll pick the garment and its colour right after.', cap: 'Generic preview', alt_front: 'Generic T-shirt, front', alt_back: 'Generic T-shirt, back', pick: 'Great! Now pick your garment and its colour.', validate: 'Confirm this garment', edit: 'Edit the design', prev: 'Previous side', next: 'Next side', views: 'T-shirt sides', which: 'Which side should I put it on?', empty: 'Nothing yet' },
    es: { ok: 'OK con este diseño', lead: 'Coloca y ajusta tus logos delante y espalda. La prenda y su color se eligen justo después.', cap: 'Vista genérica', alt_front: 'Camiseta genérica, delante', alt_back: 'Camiseta genérica, espalda', pick: '¡Genial! Ahora elige tu prenda y su color.', validate: 'Confirmar esta prenda', edit: 'Modificar el diseño', prev: 'Cara anterior', next: 'Cara siguiente', views: 'Caras de la camiseta', which: '¿En qué cara lo pongo?', empty: 'Nada por ahora' },
    de: { ok: 'OK, dieses Design', lead: 'Platziere und skaliere deine Logos vorne und hinten. Kleidungsstück und Farbe wählst du direkt danach.', cap: 'Allgemeine Vorschau', alt_front: 'Allgemeines T-Shirt, vorne', alt_back: 'Allgemeines T-Shirt, hinten', pick: 'Super! Wähle jetzt dein Kleidungsstück und seine Farbe.', validate: 'Dieses Kleidungsstück wählen', edit: 'Design ändern', prev: 'Vorherige Seite', next: 'Nächste Seite', views: 'Seiten des T-Shirts', which: 'Auf welche Seite soll es?', empty: 'Noch nichts' },
    it: { ok: 'OK per questo design', lead: 'Posiziona e regola i tuoi loghi davanti e retro. Capo e colore si scelgono subito dopo.', cap: 'Anteprima generica', alt_front: 'Maglietta generica, davanti', alt_back: 'Maglietta generica, retro', pick: 'Ottimo! Ora scegli il tuo capo e il suo colore.', validate: 'Conferma questo capo', edit: 'Modifica il design', prev: 'Lato precedente', next: 'Lato successivo', views: 'Lati della maglietta', which: 'Su quale lato lo metto?', empty: 'Ancora niente' }
  };
  var HINT = { fr: 'Glisse un logo pour le placer, tire son coin pour l’agrandir.', en: 'Drag a logo to place it, pull its corner to resize it.', es: 'Arrastra un logo para colocarlo, tira de su esquina para agrandarlo.', de: 'Zieh ein Logo, um es zu platzieren, zieh an der Ecke, um es zu vergrößern.', it: 'Trascina un logo per posizionarlo, tira l’angolo per ingrandirlo.' };

  var FACES = ['front', 'back'];
  var SIZES = { front: [28, 12], back: [28, 32] };
  var IC = {
    redo: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/></svg>',
    spark: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/></svg>',
    up: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M7 9l5-5 5 5M5 20h14"/></svg>',
    x: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    left: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>',
    right: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
  };

  /* ---------- Mockup générique : t-shirt oversize blanc cassé, une vue par face (carré 1000 × 1000) ----------
   * Corps ≈ 490 px pour ≈ 56 cm → 8,8 px/cm. Zones (% de la vue) : [gauche, haut, largeur, hauteur]. */
  function teeSvg(back) {
    var neck = back ? 'C 560 178, 440 178, 400 152' : 'C 560 222, 440 222, 400 152';
    var body = 'M 400 152 L 232 196 C 168 214, 118 292, 66 428 L 186 500 L 252 404 L 258 902 Q 500 918 742 902 L 748 404 L 814 500 L 934 428 C 882 292, 832 214, 768 196 L 600 152 ' + neck + ' Z';
    var rib = back ? 'M 400 152 C 440 178, 560 178, 600 152 M 386 156 C 432 196, 568 196, 614 156' : 'M 400 152 C 440 222, 560 222, 600 152 M 386 156 C 428 244, 572 244, 614 156';
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">' +
      '<defs><radialGradient id="bg" cx="50%" cy="42%" r="72%"><stop offset="0" stop-color="#2d2a27"/><stop offset="1" stop-color="#141312"/></radialGradient>' +
      '<linearGradient id="g" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#e4ddd0"/><stop offset=".2" stop-color="#f1ede4"/><stop offset=".5" stop-color="#f4f0e8"/><stop offset=".8" stop-color="#f0ebe2"/><stop offset="1" stop-color="#e2dbce"/></linearGradient>' +
      '<linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".25"/><stop offset=".6" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".05"/></linearGradient>' +
      '<filter id="s" x="-10%" y="-10%" width="120%" height="125%"><feDropShadow dx="0" dy="18" stdDeviation="22" flood-color="#000" flood-opacity=".5"/></filter></defs>' +
      '<rect width="1000" height="1000" fill="url(#bg)"/><g filter="url(#s)"><path d="' + body + '" fill="url(#g)" stroke="#d4cbbb" stroke-width="2.5" stroke-linejoin="round"/></g>' +
      '<path d="' + body + '" fill="url(#v)"/>' +
      '<path d="M 252 404 C 246 330, 240 260, 232 196 M 748 404 C 754 330, 760 260, 768 196" stroke="#dcd3c4" stroke-width="2.5" fill="none"/>' +
      '<path d="M 76 404 L 192 474 M 924 404 L 808 474" stroke="#ded6c8" stroke-width="2.5" fill="none"/>' +
      '<path d="M 262 880 Q 500 896 738 880" stroke="#ded6c8" stroke-width="2.5" fill="none"/>' +
      '<path d="' + rib + '" stroke="#d0c6b5" stroke-width="3" fill="none" stroke-linecap="round"/>' +
      '<path d="M 330 560 C 360 640, 350 760, 320 860 M 680 600 C 650 690, 662 790, 690 870" stroke="#e6dfd3" stroke-width="3" fill="none" stroke-linecap="round" opacity=".8"/></svg>';
  }
  var GEN_URL = { front: 'data:image/svg+xml,' + encodeURIComponent(teeSvg(false)), back: 'data:image/svg+xml,' + encodeURIComponent(teeSvg(true)) };
  var ZONE = { front: [31.79, 34.7, 34.82, 14.92], back: [32.59, 30.2, 34.82, 39.79] };

  function enhance(root) {
    var inst = root._ais;
    if (!inst || !inst._v11 || inst._v13) return;
    inst._v13 = true;
    inst.genUrl = function (side) { return GEN_URL[side]; };   // t-shirt générique (réutilisé par le choix des zones, v16)
    var t = T[inst.loc] || T.en, bt = inst.t || {}, hint = HINT[inst.loc] || HINT.en;
    var sideName = function (side) { return (bt.sides && bt.sides[side]) || (side === 'back' ? 'Dos' : 'Devant'); };

    /* ---------- Conversation : identifiant par création + étapes clés (analyse, côté serveur) ---------- */
    var baseEndpoint = inst.endpoint || '';
    var newConv = function () {
      inst.convId = uid();
      if (baseEndpoint) inst.endpoint = baseEndpoint + (baseEndpoint.indexOf('?') > -1 ? '&' : '?') + 'conv=' + inst.convId;
    };
    newConv();
    var baseReset = inst.reset;
    inst.reset = function () { var r = baseReset.apply(this, arguments); newConv(); return r; };
    inst.track = function (type, data) {
      if (!baseEndpoint || !window.fetch) return;
      try {
        fetch(baseEndpoint, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'event', conv: inst.convId, locale: inst.loc, type: type, data: data || {} }) }).catch(function () {});
      } catch (e) {}
    };
    var facesInfo = function () {
      var s = inst.s, out = {};
      FACES.forEach(function (f) { var x = s.faces[f]; out[f] = x ? (x.own ? 'import' : (x.mock ? 'simule' : 'manu')) : null; });
      return out;
    };

    /* ---------- Définition (dpi) : seulement pour un fichier importé par le client ---------- */
    var baseQuality = inst.refreshQuality;
    inst.refreshQuality = function () {
      baseQuality.apply(this, arguments);
      var q = this.resultEl && this.resultEl.querySelector('[data-quality]'), f = this.s.faces[this.s.edit];
      if (q && f && !f.own) { q.textContent = ''; q.classList.remove('ais-own__q--low'); }
    };

    /* ---------- Étape 1 : slider Devant / Dos sur le mockup générique ---------- */
    inst.logoBoxV13 = function (side) {
      var b = this.logoBox(side);
      return 'left:' + b.left.toFixed(2) + '%;top:' + b.top.toFixed(2) + '%;width:' + b.width.toFixed(2) + '%;height:' + b.height.toFixed(2) + '%';
    };
    inst.genSlide = function (side) {
      var s = this.s, f = s.faces[side], cur = s.edit === side, name = sideName(side), z = ZONE[side];
      var canAi = s.path === 'story' && !!s.idea && !!this.endpoint;
      var inner = '';
      if (f) {
        var b = this.logoBox(side);
        inner = '<img class="ais-zone__logo" data-v11-drag="' + side + '"' + (cur ? ' tabindex="0"' : '') + ' role="img" aria-label="' + esc(name) + ' : ' + esc(hint) + '" src="' + esc(f.view || this.mockPng(side)) + '" alt="" draggable="false" style="' + this.logoBoxV13(side) + '">' +
          '<span class="ais-zone__handle" data-v11-size="' + side + '" aria-hidden="true" style="left:' + (b.left + b.width).toFixed(2) + '%;top:' + (b.top + b.height).toFixed(2) + '%"></span>';
      } else {
        inner = '<span class="ais-zone__empty" aria-hidden="true">+</span>';
      }
      var zone = '<div class="ais-zone' + (cur ? ' is-sel' : '') + (f ? '' : ' is-empty') + '" data-v11-zone="' + side + '" style="left:' + z[0] + '%;top:' + z[1] + '%;width:' + z[2] + '%;height:' + z[3] + '%">' + inner + '</div>';
      var bar = '<div class="ais-gen__bar" role="group" aria-label="' + esc(name) + '">' +
        '<b class="ais-gen__name">' + esc(name) + '</b>' +
        (canAi ? '<button type="button" class="ais-zone__btn' + (f ? '' : ' ais-zone__btn--main') + '" data-act="face-redo" data-v11-face="' + side + '">' + (f ? IC.redo : IC.spark) + '<span>' + esc(f ? (inst._v11t('e_redo') || 'Refaire') : (inst._v11t('e_create') || 'Créer avec Manu')) + '</span></button>' : '') +
        '<label class="ais-zone__btn" data-v11-face="' + side + '">' + IC.up + '<span>' + esc(inst._v11t('e_upload') || 'Importer') + '</span><input type="file" class="ais-vh" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg" data-face-file></label>' +
        (f ? '<button type="button" class="ais-zone__btn" data-act="face-clear" data-v11-face="' + side + '">' + IC.x + '<span class="ais-vh">' + esc(inst._v11t('e_remove') || 'Retirer') + '</span></button>' : '') +
        '</div>';
      return '<div class="ais-gen__slide ais-stage ais-stage--gen' + (cur ? ' is-cur' : '') + '" data-v11-stage data-v13-slide="' + side + '"' + (cur ? '' : ' aria-hidden="true" inert') + '>' +
        '<img class="ais-stage__base" src="' + esc(GEN_URL[side]) + '" alt="' + esc(t['alt_' + side]) + '" width="1000" height="1000">' + zone +
        '<span class="ais-stage__cap">' + esc(t.cap) + '</span>' + bar + '</div>';
    };
    inst.genStage = function () {
      var self = this, s = this.s, idx = FACES.indexOf(s.edit) < 0 ? 0 : FACES.indexOf(s.edit);
      var tabs = FACES.map(function (side) {
        return '<button type="button" class="ais-gen__tab" data-v13-tab="' + side + '" aria-pressed="' + (s.edit === side) + '">' + esc(sideName(side)) +
          '<span class="ais-gen__dot' + (s.faces[side] ? ' is-on' : '') + '" aria-hidden="true"></span></button>';
      }).join('');
      return '<div class="ais-gen" data-v13-gen>' +
        '<div class="ais-gen__tabs" role="group" aria-label="' + esc(t.views) + '">' + tabs + '</div>' +
        '<div class="ais-gen__view" data-v13-view>' +
          '<div class="ais-gen__track" style="transform:translateX(' + (-100 * idx) + '%)">' + FACES.map(function (f) { return self.genSlide(f); }).join('') + '</div>' +
          '<button type="button" class="ais-gen__nav ais-gen__nav--prev" data-v13-nav="-1" aria-label="' + esc(t.prev) + '"' + (idx === 0 ? ' disabled' : '') + '>' + IC.left + '</button>' +
          '<button type="button" class="ais-gen__nav ais-gen__nav--next" data-v13-nav="1" aria-label="' + esc(t.next) + '"' + (idx === FACES.length - 1 ? ' disabled' : '') + '>' + IC.right + '</button>' +
        '</div></div>';
    };
    // Textes v11 (Refaire, Importer…) : repris de ses traductions si exposées, sinon français
    inst._v11t = function (k) {
      var L = { fr: { e_redo: 'Refaire', e_create: 'Créer avec Manu', e_upload: 'Importer', e_remove: 'Retirer' },
        en: { e_redo: 'Redo', e_create: 'Create with Manu', e_upload: 'Upload', e_remove: 'Remove' },
        es: { e_redo: 'Rehacer', e_create: 'Crear con Manu', e_upload: 'Subir', e_remove: 'Quitar' },
        de: { e_redo: 'Neu', e_create: 'Mit Manu gestalten', e_upload: 'Hochladen', e_remove: 'Entfernen' },
        it: { e_redo: 'Rifai', e_create: 'Crea con Manu', e_upload: 'Carica', e_remove: 'Togli' } };
      return (L[inst.loc] || L.en)[k];
    };
    // Passer d'une face à l'autre sans redessiner (les logos et leur position restent)
    inst.showFace = function (side) {
      var el = this.resultEl, g = el && el.querySelector('[data-v13-gen]'); if (!g || FACES.indexOf(side) < 0) return;
      var idx = FACES.indexOf(side);
      this.s.edit = side;
      g.querySelector('.ais-gen__track').style.transform = 'translateX(' + (-100 * idx) + '%)';
      g.querySelectorAll('[data-v13-tab]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-v13-tab') === side)); });
      g.querySelectorAll('[data-v13-slide]').forEach(function (sl) {
        var on = sl.getAttribute('data-v13-slide') === side;
        sl.classList.toggle('is-cur', on);
        if (on) { sl.removeAttribute('aria-hidden'); sl.removeAttribute('inert'); } else { sl.setAttribute('aria-hidden', 'true'); sl.setAttribute('inert', ''); }
        var lg = sl.querySelector('[data-v11-drag]'); if (lg) { if (on) lg.setAttribute('tabindex', '0'); else lg.removeAttribute('tabindex'); }
        var z = sl.querySelector('[data-v11-zone]'); if (z) z.classList.toggle('is-sel', on);
      });
      g.querySelector('[data-v13-nav="-1"]').disabled = idx === 0;
      g.querySelector('[data-v13-nav="1"]').disabled = idx === FACES.length - 1;
      this.refreshQuality();
    };

    var v11Render = inst.renderResult;
    inst.renderResult = function () {
      var el = this.resultEl; if (!el) return;
      var s = this.s;
      // Étape 2 : éditeur v11 (photos Shopify, vêtement, couleur), logos figés
      if (s.designOk) {
        el.classList.remove('ais-result--gen');
        v11Render.apply(this, arguments);
        if (!el.querySelector('[data-v11-stage]')) return;
        el.classList.add('ais-result--v13sup');
        var keep = el.querySelector('[data-act="keep"]');
        if (keep) keep.innerHTML = esc(t.validate) + keep.innerHTML.replace(/^[^<]*/, '');
        el.querySelectorAll('.ais-side > .ais-note:last-child').forEach(function (n) { n.remove(); });
        el.querySelectorAll('[data-v11-drag]').forEach(function (n) { n.removeAttribute('tabindex'); n.removeAttribute('data-v11-drag'); });
        el.querySelectorAll('[data-v11-size]').forEach(function (n) { n.removeAttribute('data-v11-size'); });
        return;
      }
      // Étape 1 : slider Devant / Dos
      if (FACES.indexOf(s.edit) < 0) s.edit = 'front';
      el.classList.add('ais-result--studio', 'ais-result--gen');
      el.innerHTML = this.genStage() +
        '<div class="ais-result__body ais-side">' +
          '<p class="ais-gen__lead">' + esc(t.lead) + '</p>' +
          '<p class="ais-note" data-quality></p>' +
          (this.delayLine ? this.delayLine('') : '') +
          '<div class="ais-actions"><button type="button" class="ais-btn ais-btn--primary" data-act="v13-ok">' + esc(t.ok) + IC.arrow + '</button></div>' +
          '<p class="ais-note">' + esc(hint) + '</p>' +
        '</div>';
      this.refreshQuality();
    };
    // Onglets, flèches, glissement (hors logo, poignée et boutons)
    inst.thread.addEventListener('click', function (e) {
      var tb = e.target.closest('[data-v13-tab]');
      if (tb && inst.resultEl && inst.resultEl.contains(tb)) { inst.showFace(tb.getAttribute('data-v13-tab')); return; }
      var nv = e.target.closest('[data-v13-nav]');
      if (nv && inst.resultEl && inst.resultEl.contains(nv)) {
        var i = FACES.indexOf(inst.s.edit) + (+nv.getAttribute('data-v13-nav'));
        if (i >= 0 && i < FACES.length) inst.showFace(FACES[i]);
      }
    });
    var swipe = null;
    inst.thread.addEventListener('pointerdown', function (e) {
      var v = e.target.closest('[data-v13-view]');
      if (!v || !inst.resultEl || !inst.resultEl.contains(v) || inst.busy) return;
      if (e.target.closest('[data-v11-drag], [data-v11-size], button, label, input')) return;
      swipe = { x: e.clientX, y: e.clientY };
    });
    window.addEventListener('pointerup', function (e) {
      if (!swipe) return;
      var dx = e.clientX - swipe.x, dy = e.clientY - swipe.y; swipe = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      var i = FACES.indexOf(inst.s.edit) + (dx < 0 ? 1 : -1);
      if (i >= 0 && i < FACES.length) inst.showFace(FACES[i]);
    });
    window.addEventListener('pointercancel', function () { swipe = null; });

    /* ---------- « OK pour ce design » → étape 2 (vêtement + couleur) ---------- */
    inst.toSupport = function () {
      var self = this, s = this.s;
      s.designOk = true; s.support = null; s.pending = null;
      this.setStep('support');
      this.bot(esc(t.pick)).then(function () {
        return self.bot('<div class="ais-result ais-result--stack" data-result></div>', true);
      }).then(function (el) {
        if (!el) return;
        self.resultEl = el.querySelector('[data-result]');
        self.renderResult();
        // Sans photo pour ce sport : liste classique des vêtements
        if (!self.resultEl.querySelector('[data-v11-stage]')) { el.remove(); self.resultEl = null; self.flow(); return; }
        s.pending = null;
        self.chips([{ cmd: 'change-design', l: bt.change_design || t.edit, ghost: true }]);
      });
    };
    inst.thread.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act="v13-ok"]'); if (!b || inst.busy) return;
      var s = inst.s;
      if (!s.faces.front && !s.faces.back) { inst.bot(esc(bt.nothing_to_keep || '')); return; }
      inst.user(t.ok); inst.lockThread(); inst.resultEl = null; inst.quick.innerHTML = '';
      inst.track('design_ok', { faces: facesInfo(), pos: s.pos, idea: s.idea || null, style: s.style || null });
      inst.saveFaces().then(function () { inst.toSupport(); });
    });
    // Étapes suivantes (analyse) : vêtement validé, passage au configurateur
    inst.thread.addEventListener('click', function (e) {
      var s = inst.s;
      if (e.target.closest('[data-act="keep"]') && s.designOk && s.support) inst.track('support_ok', { product: s.support.h, color: s.color });
      if (e.target.closest('[data-act="handoff"]')) inst.track('handoff', { product: s.support && s.support.h, color: s.color, faces: facesInfo() });
    }, true);

    /* ---------- Commandes : face à choisir pour une image à imprimer, retour à l'étape 2 ---------- */
    var askFace = function (file) {
      inst._printFile = file;
      inst.bot(esc(t.which)).then(function () {
        inst.chips([{ cmd: 'v13-print', v: 'front', l: sideName('front') }, { cmd: 'v13-print', v: 'back', l: sideName('back') }]);
      });
    };
    var baseCommand = inst.command;
    inst.command = function (cmd, value, label) {
      var s = this.s;
      if (cmd === 'img-print') {
        var att = this.attached; if (!att) return;
        this.user(label); this.quick.innerHTML = ''; this.lockThread(); this.attached = null;
        this.track('image_choice', { use: 'print' });
        return askFace(att.file);
      }
      if (cmd === 'img-inspire') this.track('image_choice', { use: 'inspire' });
      if (cmd === 'v13-print') {
        var f = this._printFile; this._printFile = null;
        this.user(label); this.quick.innerHTML = '';
        if (!f) return;
        this.track('print_face', { side: value });
        this._quietUser = true;
        return baseOwnFile.call(this, f, value === 'back' ? 'back' : 'front', false);
      }
      if (cmd === 'change-support' && s.path !== 'design' && s.designOk) {
        this.user(label); this.quick.innerHTML = ''; this.lockThread(); this.resultEl = null;
        return this.toSupport();
      }
      return baseCommand.apply(this, arguments);
    };
    // Import depuis l'accueil (« Importer mon logo », « + Image › Mon logo à imprimer ») : la face est demandée
    var baseOwnFile = inst.ownFile;
    inst.ownFile = function (file, side, fromHero) {
      if (!fromHero) return baseOwnFile.apply(this, arguments);
      this.open({ path: 'own', own: true });
      this.user(file.name);
      this.track('image_choice', { use: 'print', from: 'accueil' });
      askFace(file);
    };
  }

  // La v11 doit être passée avant : sinon on réessaie un peu plus tard
  function boot(n) {
    n = typeof n === 'number' ? n : 0;
    document.querySelectorAll('[data-ais]').forEach(function (root) {
      if (root._ais && root._ais._v11) enhance(root);
      else if (n < 40) setTimeout(function () { boot(n + 1); }, 150);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(0); }); else boot(0);
  document.addEventListener('shopify:section:load', function () { setTimeout(function () { boot(0); }, 10); });
})();
